import React, { useEffect, useState } from "react";
import { MapPin, X, Search } from "lucide-react";
import useEscape from "../lib/useEscape";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function CitySelector() {
  const { city, setCity } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cities, setCities] = useState([]);
  useEscape(open, () => setOpen(false));

  useEffect(() => {
    const id = setTimeout(async () => {
      const res = await api.get("/cities", { params: { q, limit: 80 } });
      setCities(res.data.cities || []);
    }, 150);
    return () => clearTimeout(id);
  }, [q]);

  const pick = (c) => {
    setCity(c);
    setOpen(false);
    setQ("");
  };

  return (
    <>
      <button
        data-testid="city-selector-btn"
        onClick={() => setOpen(true)}
        aria-label={city ? `City: ${city}. Change city` : "Select city"}
        className="flex items-center gap-1.5 text-[13px] font-semibold h-10 px-2.5 sm:px-3 border border-border bg-card hover:border-[var(--bhega-border-strong)] transition-colors"
      >
        <MapPin size={14} className="text-brand shrink-0" />
        <span className="max-w-[84px] sm:max-w-[120px] truncate">{city ? city.split(",")[0] : "Select city"}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-background/40" onClick={() => setOpen(false)}>
          <div
            className="bg-card w-full max-w-xl rounded-none border border-border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="city-selector-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg">Choose your city</h3>
              <button onClick={() => setOpen(false)} data-testid="city-selector-close" className="p-1 hover:bg-foreground/10 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-border">
              <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-none focus-within:border-[#FFBE06]">
                <Search size={14} className="text-muted-foreground" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Type any city, town or village in India…"
                  className="flex-1 outline-none text-sm"
                  data-testid="city-selector-input"
                />
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {cities.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">No matching cities. Try a different keyword.</p>
              )}
              <ul className="divide-y divide-border">
                {cities.map((c) => (
                  <li key={c}>
                    <button
                      onClick={() => pick(c)}
                      data-testid={`city-option-${c.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                      className={`w-full text-left px-5 py-3 text-sm hover:bg-foreground/10 flex items-center gap-2 ${city === c ? "bg-[#FFBE06]/10 font-semibold" : ""}`}
                    >
                      <MapPin size={14} className="text-muted-foreground" />
                      {c}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {city && (
              <div className="px-5 py-3 border-t border-border flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Current: <strong>{city}</strong></span>
                <button
                  onClick={() => { setCity(""); setOpen(false); }}
                  data-testid="city-clear-btn"
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
